/**
 * posthog.ts — Analytics module for wejha-content-creator
 *
 * Tracking lifecycle:
 *   • PostHog is initialised at startup but starts OPTED-OUT by default.
 *     No events are sent before the user logs in (login page, etc. are ignored).
 *   • On login  → posthog.opt_in_capturing()  + posthog.identify()  fired.
 *   • On logout → posthog.opt_out_capturing() + posthog.reset()     fired.
 *
 * User identity is read from localStorage keys written by authSlice.ts:
 *   - "user"        → { id, name, email, companyId, company?: { id, name } }
 *   - "accessToken" → presence indicates an active session
 *
 * All capture helpers are safe no-ops when PostHog is not initialised or
 * when the user is opted-out, so they never need to be null-guarded at
 * the call site.
 */

import posthog from 'posthog-js';

// ─── Module-level state ───────────────────────────────────────────────────────

let hasInitialized = false;
/** Track which distinct ID we last called posthog.identify() with so we never
 *  double-identify within the same session. */
let identifiedDistinctId: string | null = null;

// ─── Auth shape (mirrors authSlice.ts) ───────────────────────────────────────

interface StoredUser {
  id: string;
  name: string;
  email: string;
  companyId: string;
  company?: { id: string; name: string; logo?: string };
}

interface Actor {
  actorUserId: string | undefined;
  actorEmail: string | undefined;
  actorName: string | undefined;
  actorCompanyId: string | undefined;
  actorCompanyName: string | undefined;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Read user details from localStorage.
 * authSlice persists the user object under the key "user".
 */
function getActorFromStorage(): Actor {
  try {
    const raw = localStorage.getItem('user');
    const user: StoredUser | null = raw ? JSON.parse(raw) : null;
    return {
      actorUserId: user?.id,
      actorEmail: user?.email,
      actorName: user?.name,
      actorCompanyId: user?.companyId ?? user?.company?.id,
      actorCompanyName: user?.company?.name,
    };
  } catch {
    return {
      actorUserId: undefined,
      actorEmail: undefined,
      actorName: undefined,
      actorCompanyId: undefined,
      actorCompanyName: undefined,
    };
  }
}

/**
 * Identify the current user with PostHog and opt-in to capturing — idempotent.
 * Uses the user's server-side ID as the distinct ID (falls back to email).
 *
 * This is the ONLY place opt_in_capturing() is called, ensuring tracking
 * begins only when a valid authenticated user is confirmed.
 */
function identifyFromStorage(): void {
  if (!hasInitialized) return;

  const { actorUserId, actorEmail, actorName, actorCompanyId, actorCompanyName } =
    getActorFromStorage();

  const distinctId = String(actorUserId || actorEmail || '').trim();
  if (!distinctId) return; // no user in storage — stay opted-out

  // Opt in first so the identify call itself is also captured.
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing();
  }

  if (identifiedDistinctId === distinctId) return; // already identified

  posthog.identify(distinctId, {
    // PostHog reserved properties (shown in the UI)
    $email: actorEmail,
    $name: actorName,
    // Explicit copies for easy filtering
    email: actorEmail,
    name: actorName,
    actorUserId,
    companyId: actorCompanyId,
    companyName: actorCompanyName,
  });

  identifiedDistinctId = distinctId;
}

// ─── Initialisation ───────────────────────────────────────────────────────────

/**
 * Initialise PostHog analytics.
 * Must be called once before the React tree renders (see main.tsx).
 *
 * Requires the following env vars:
 *   VITE_POSTHOG_KEY   — project API key
 *   VITE_POSTHOG_HOST  — ingestion host (default: https://app.posthog.com)
 */
export function initPostHog(): void {
  if (hasInitialized) return;

  // ── Production guard ───────────────────────────────────────────────────────
  // PostHog is intentionally disabled in development and staging environments.
  // Set VITE_APP_ENV=PRODUCTION in your production .env to enable it.
  if (import.meta.env.VITE_APP_ENV !== 'PRODUCTION') {
    console.info('[PostHog] Skipped — VITE_APP_ENV is not PRODUCTION.');
    return;
  }

  const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const apiHost = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://posthog.wejha.com';

  if (!apiKey || apiKey === 'your_posthog_project_api_key_here') {
    console.warn('[PostHog] VITE_POSTHOG_KEY is not set — analytics disabled.');
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost,

    // ── Super-properties ────────────────────────────────────────────────────
    // Attached to every event — useful for filtering in a shared PostHog org.
    loaded(ph) {
      ph.register({ app_name: 'wejha-content-creator' });
    },

    // ── Opt-out by default ───────────────────────────────────────────────────
    // PostHog starts SILENT. No events (including autocapture) are sent until
    // the user logs in and identifyFromStorage() calls opt_in_capturing().
    // This prevents login-page interactions from ever being tracked.
    opt_out_capturing_by_default: true,

    // ── Autocapture ─────────────────────────────────────────────────────────
    autocapture: true,       // clicks, inputs, form submissions, etc.
    capture_pageview: true,  // $pageview fired automatically
    capture_pageleave: true, // $pageleave fired automatically

    // ── Persistence / Privacy ───────────────────────────────────────────────
    persistence: 'localStorage+cookie',
    respect_dnt: false,

    // ── Debug ───────────────────────────────────────────────────────────────
    debug: false,
  });

  hasInitialized = true;

  // If a user is already logged in (page refresh / returning visit), identify them immediately.
  identifyFromStorage();
}

// ─── Auth events ─────────────────────────────────────────────────────────────

/**
 * Call this right after login credentials have been saved to Redux / localStorage.
 * Identifies the user in PostHog and fires a `user_logged_in` event.
 */
export function captureUserLoggedIn(properties: Record<string, unknown> = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('user_logged_in', {
    ...getActorFromStorage(),
    ...properties,
  });
}

/**
 * Call this on logout — resets the PostHog session so the next user gets a
 * fresh anonymous identity.
 */
export function resetPostHog(): void {
  if (!hasInitialized) return;
  identifiedDistinctId = null;
  // Stop all capturing immediately — no events will be sent after this point
  // until the next login triggers opt_in_capturing() again.
  posthog.opt_out_capturing();
  posthog.reset();
}

// ─── Template events ──────────────────────────────────────────────────────────

export interface TemplateEventProperties {
  templateId?: string;
  templateName?: string;
  category?: string;
  industry?: string;
  orientation?: string;
  designStyle?: string;
  animationType?: string;
  [key: string]: unknown;
}

/** User viewed the template gallery / applied filters. */
export function captureTemplateBrowsed(properties: TemplateEventProperties = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('template_browsed', { ...getActorFromStorage(), ...properties });
}

/** User opened a template to use it as the base for a new design. */
export function captureTemplateSelected(properties: TemplateEventProperties = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('template_selected', { ...getActorFromStorage(), ...properties });
}

// ─── Design / saved-template events ──────────────────────────────────────────

export interface DesignEventProperties {
  designId?: string;
  designName?: string;
  category?: string;
  orientation?: string;
  isSavedToCms?: boolean;
  [key: string]: unknown;
}

/** User saved a new design for the first time. */
export function captureDesignCreated(properties: DesignEventProperties = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('design_created', { ...getActorFromStorage(), ...properties });
}

/** User saved changes to an existing design. */
export function captureDesignUpdated(properties: DesignEventProperties = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('design_updated', { ...getActorFromStorage(), ...properties });
}

/** User deleted a saved design. */
export function captureDesignDeleted(properties: DesignEventProperties = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('design_deleted', { ...getActorFromStorage(), ...properties });
}

/**
 * User pushed a design to the CMS as a content item.
 * `isSavedToCms` distinguishes a first-time push from subsequent updates.
 */
export function captureDesignPublishedToCms(properties: DesignEventProperties = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('design_published_to_cms', { ...getActorFromStorage(), ...properties });
}

// ─── Template-editor events ───────────────────────────────────────────────────

export interface EditorEventProperties {
  designId?: string;
  action?: string;
  element?: string; // 'text' | 'image' | 'shape' | 'background' | 'scroller' | …
  [key: string]: unknown;
}

/**
 * Generic editor interaction helper.
 * Builds an event name from module + action, mirroring the Signage-CMS pattern.
 *
 * @example
 * captureEditorAction({ module: 'image', action: 'upload' })
 * // fires → "image_uploaded"
 */
export function captureEditorAction(
  properties: EditorEventProperties & { module?: string } = {},
): void {
  if (!hasInitialized) return;
  identifyFromStorage();

  const modulePart = normalizeEventPart(properties.module);
  const actionPart = mapEditorAction(properties.action);
  const eventName =
    [modulePart, actionPart].filter(Boolean).join('_') || 'editor_action_performed';

  posthog.capture(eventName, { ...getActorFromStorage(), ...properties });
}

// ─── Brand-Kit events ─────────────────────────────────────────────────────────

export interface BrandKitEventProperties {
  brandKitId?: string;
  brandKitName?: string;
  section?: 'logo' | 'colors' | 'typography';
  [key: string]: unknown;
}

export function captureBrandKitEvent(
  eventName: string,
  properties: BrandKitEventProperties = {},
): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture(eventName, { ...getActorFromStorage(), ...properties });
}

// ─── AI Generator events ──────────────────────────────────────────────────────

export interface AiGeneratorEventProperties {
  prompt?: string;
  industry?: string;
  orientation?: string;
  designStyle?: string;
  resultCount?: number;
  selectedTemplateId?: string;
  [key: string]: unknown;
}

/** User submitted a prompt to the AI generator. */
export function captureAiGenerationStarted(properties: AiGeneratorEventProperties = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('ai_generation_started', { ...getActorFromStorage(), ...properties });
}

/** User selected one of the AI-suggested templates to edit. */
export function captureAiTemplateSelected(properties: AiGeneratorEventProperties = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('ai_template_selected', { ...getActorFromStorage(), ...properties });
}

// ─── CMS asset events ─────────────────────────────────────────────────────────

export interface AssetEventProperties {
  contentId?: string;
  contentName?: string;
  fileType?: string;
  fileSize?: number;
  orientation?: string;
  [key: string]: unknown;
}

/** User uploaded or saved an asset to the CMS content library. */
export function captureAssetUploadedToCms(properties: AssetEventProperties = {}): void {
  if (!hasInitialized) return;
  identifyFromStorage();
  posthog.capture('cms_asset_uploaded', { ...getActorFromStorage(), ...properties });
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function normalizeEventPart(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/, '');
}

function mapEditorAction(action: unknown): string {
  const n = normalizeEventPart(action);
  const map: Record<string, string> = {
    view: 'viewed',
    edit: 'edited',
    create: 'created',
    generate: 'generated',
    delete: 'deleted',
    save: 'saved',
    upload: 'uploaded',
    download: 'downloaded',
    export: 'exported',
    publish: 'published',
    duplicate: 'duplicated',
    preview: 'previewed',
  };
  return map[n] ?? n ?? 'used';
}

// ─── Re-export posthog for components using usePostHog() ─────────────────────

export { posthog };
